import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Activity,
  Calendar,
  Check,
  FileText,
  ListChecks,
  MessageCircle,
  MessageSquare,
  Pencil,
  ThumbsUp,
  X,
} from 'lucide-react'
import { PageContainer } from '@/components/ui/page-container'
import { PageHeader } from '@/components/ui/page-header'
import { PageHeaderLeading } from '@/components/navigation/page-header-leading'
import { PageHeaderStack } from '@/components/navigation/page-header-stack'
import { EntityManageSection } from '@/components/ui/entity-manage-section'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Badge } from '@/components/ui/badge'
import { authorizedFetch } from '@/lib/api'
import type { ThreadFilePreview, ThreadObjectPreview } from '@/hooks/use-threads'
import { ThreadSummary } from './thread-summary'
import { ThreadFeedback } from './thread-feedback'
import { ThreadFilesPanel } from './thread-files-panel'
import { ThreadHistorySection } from './thread-history-section'
import { ThreadActivityBadges } from './thread-meta'
import {
  ThreadFileChips,
  ThreadObjectChips,
  ThreadUpdatesDigest,
} from './thread-glance'
import { RelativeTime } from './relative-time'
import { formatAbsoluteDateTime } from '@/utils/relative-time'
import type { ThreadHistoryMessage } from '@/utils/thread-history'

export interface ThreadDetailPageProps {
  onOpenInAgent: (threadId: string) => void
}

type ThreadDetailMessage = ThreadHistoryMessage

type ThreadDetailResponse = {
  id: string
  subject: string | null
  user_id: string | null
  created_at: string
  updated_at: string
  messages: ThreadDetailMessage[]
  summary?: string | null
  updates_digest?: string[] | null
  activity?: Record<string, number> | null
  latest_object_update?: {
    object_type: string
    object_id: string
    object_name?: string
    action: string
    source_tool?: string
    text?: string
  } | null
  feedback_items?: Array<{
    score: number
    tags: string[]
    comment: string
    at: string | null
    message_id: string
    run_id: string
  }> | null
  file_count?: number
  files?: ThreadFilePreview[]
  object_count?: number
  objects?: ThreadObjectPreview[]
}

export function ThreadDetailPage({ onOpenInAgent }: ThreadDetailPageProps) {
  const { threadId } = useParams<{ threadId: string }>()
  const [searchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const [renaming, setRenaming] = useState(false)
  const [titleDraft, setTitleDraft] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const renameInputRef = useRef<HTMLInputElement>(null)

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['thread', threadId],
    queryFn: async () => {
      if (!threadId) throw new Error('Missing thread id')
      const r = await authorizedFetch(`/api/threads/${threadId}`)
      if (!r.ok) throw new Error(r.status === 404 ? 'Thread not found' : 'Failed to load thread')
      return (await r.json()) as ThreadDetailResponse
    },
    enabled: Boolean(threadId),
  })

  useEffect(() => {
    setTitleDraft(data?.subject || '')
  }, [data?.subject, data?.id])

  useEffect(() => {
    if (renaming) renameInputRef.current?.focus()
  }, [renaming])

  const saveTitle = useCallback(async () => {
    if (!threadId || !data) return
    const trimmed = titleDraft.trim()
    if (!trimmed || trimmed === (data.subject || '')) {
      setRenaming(false)
      return
    }
    setIsSaving(true)
    try {
      const r = await authorizedFetch(`/api/threads/${threadId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subject: trimmed }),
      })
      if (!r.ok) throw new Error('Failed to update title')
      await queryClient.invalidateQueries({ queryKey: ['threads'] })
      await queryClient.invalidateQueries({ queryKey: ['thread', threadId] })
      await refetch()
      setRenaming(false)
    } catch (err) {
      console.error('Failed to save title:', err)
    } finally {
      setIsSaving(false)
    }
  }, [threadId, data, titleDraft, queryClient, refetch])

  if (!threadId) {
    return (
      <PageContainer>
        <p className="text-muted-foreground">Invalid thread.</p>
      </PageContainer>
    )
  }

  if (isLoading) return <DetailSkeleton />

  if (error || !data) {
    return (
      <PageContainer>
        <PageHeaderStack compact>
          <PageHeader
            leading={<PageHeaderLeading back={{ label: 'Back to threads', to: '/threads' }} />}
          />
        </PageHeaderStack>
        <div className="rounded-md border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          <div className="flex items-center justify-between gap-2">
            <span>{error instanceof Error ? error.message : 'Could not load thread.'}</span>
            <Button size="sm" variant="outline" onClick={() => void refetch()}>
              Retry
            </Button>
          </div>
        </div>
      </PageContainer>
    )
  }

  const messageCount = data.messages?.length ?? 0
  const threadInChat = searchParams.get('thread') === data.id
  const openInChat = () => onOpenInAgent(data.id)
  const title = data.subject || 'Untitled thread'

  const headerActions = (
    <div className="flex flex-wrap items-center gap-2">
      {renaming ? (
        <>
          <Input
            ref={renameInputRef}
            value={titleDraft}
            onChange={(e) => setTitleDraft(e.target.value)}
            disabled={isSaving}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                void saveTitle()
              } else if (e.key === 'Escape') {
                setTitleDraft(data.subject || '')
                setRenaming(false)
              }
            }}
            className="h-9 w-56 text-sm font-semibold sm:w-72"
            aria-label="Thread title"
          />
          <Button
            size="iconSm"
            variant="ghost"
            onClick={() => void saveTitle()}
            disabled={isSaving}
            aria-label="Save title"
          >
            <Check className="h-4 w-4" />
          </Button>
          <Button
            size="iconSm"
            variant="ghost"
            onClick={() => {
              setTitleDraft(data.subject || '')
              setRenaming(false)
            }}
            aria-label="Cancel rename"
          >
            <X className="h-4 w-4" />
          </Button>
        </>
      ) : (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghostToolbar"
              size="iconSm"
              onClick={() => setRenaming(true)}
              aria-label="Rename thread"
            >
              <Pencil className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Rename</TooltipContent>
        </Tooltip>
      )}
      {threadInChat ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex">
              <Button disabled>
                <MessageSquare className="h-4 w-4" />
                Open in chat
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent>This thread is already loaded in chat</TooltipContent>
        </Tooltip>
      ) : (
        <Button onClick={openInChat}>
          <MessageSquare className="h-4 w-4" />
          Open in chat
        </Button>
      )}
    </div>
  )

  const metaDescription = (
    <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
      <span>
        <RelativeTime value={data.updated_at} prefix="Updated" />
      </span>
      <span aria-hidden>·</span>
      <span>
        <span className="tabular-nums">{messageCount}</span>{' '}
        {messageCount === 1 ? 'message' : 'messages'}
      </span>
      {(data.file_count ?? 0) > 0 ? (
        <>
          <span aria-hidden>·</span>
          <span>
            <span className="tabular-nums">{data.file_count}</span>{' '}
            {data.file_count === 1 ? 'file' : 'files'}
          </span>
        </>
      ) : null}
    </span>
  )

  return (
    <PageContainer>
      <PageHeaderStack compact>
        <PageHeader
          leading={<PageHeaderLeading back={{ label: 'Back to threads', to: '/threads' }} />}
          title={renaming ? undefined : title}
          description={renaming ? undefined : metaDescription}
          icon={<MessageSquare className="h-5 w-5" />}
          actions={headerActions}
        />
      </PageHeaderStack>

      <div className="grid grid-cols-1 gap-6 @3col:grid-cols-3">
        <div className="space-y-6 @3col:col-span-2">
          <EntityManageSection title="Summary" leadingIcon={MessageSquare}>
            <ThreadSummary summary={data.summary ?? null} variant="card" />
          </EntityManageSection>

          {(data.updates_digest && data.updates_digest.length > 0) ||
          data.latest_object_update ? (
            <EntityManageSection title="Updates" leadingIcon={ListChecks}>
              <div className="space-y-3">
                <ThreadUpdatesDigest updates={data.updates_digest} />
                {data.latest_object_update ? (
                  <LatestObjectUpdate update={data.latest_object_update} />
                ) : null}
              </div>
            </EntityManageSection>
          ) : null}

          <ThreadHistorySection
            threadId={data.id}
            messages={data.messages}
            feedbackItems={data.feedback_items}
          />

          {(data.objects && data.objects.length > 0) ||
          (data.activity && Object.keys(data.activity).length > 0) ? (
            <EntityManageSection title="Objects" leadingIcon={Activity}>
              <div className="space-y-3">
                <ThreadObjectChips
                  objects={data.objects}
                  activity={data.activity}
                  objectCount={data.object_count}
                />
                {data.activity && Object.keys(data.activity).length > 0 ? (
                  <ThreadActivityBadges activity={data.activity} max={Infinity} size="md" />
                ) : null}
              </div>
            </EntityManageSection>
          ) : null}

          {data.feedback_items && data.feedback_items.length > 0 ? (
            <EntityManageSection title="Feedback" leadingIcon={ThumbsUp}>
              <ThreadFeedback feedbackItems={data.feedback_items} />
            </EntityManageSection>
          ) : null}
        </div>

        <aside className="space-y-6">
          <EntityManageSection
            title="Files"
            leadingIcon={FileText}
            subtitle={
              (data.file_count ?? 0) > 0
                ? `${data.file_count} in this thread`
                : 'Upload files related to this work'
            }
          >
            {(data.files && data.files.length > 0) || (data.file_count ?? 0) > 0 ? (
              <div className="mb-3">
                <ThreadFileChips files={data.files} fileCount={data.file_count} />
              </div>
            ) : null}
            <ThreadFilesPanel threadId={data.id} embedded />
          </EntityManageSection>

          <EntityManageSection title="Details" leadingIcon={Calendar}>
            <div className="space-y-3">
              <KV
                label="Created"
                value={
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="tabular-nums">
                        <RelativeTime value={data.created_at} />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>{formatAbsoluteDateTime(data.created_at)}</TooltipContent>
                  </Tooltip>
                }
              />
              <KV
                label="Updated"
                value={
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="tabular-nums">
                        <RelativeTime value={data.updated_at} />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>{formatAbsoluteDateTime(data.updated_at)}</TooltipContent>
                  </Tooltip>
                }
              />
              <KV
                label="Messages"
                value={
                  <span className="inline-flex items-center gap-1.5 tabular-nums">
                    <MessageCircle className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
                    {messageCount}
                  </span>
                }
              />
            </div>
          </EntityManageSection>
        </aside>
      </div>
    </PageContainer>
  )
}

function LatestObjectUpdate({
  update,
}: {
  update: NonNullable<ThreadDetailResponse['latest_object_update']>
}) {
  const label = update.object_name || update.object_id
  return (
    <div className="space-y-2">
      <p className="text-xs font-medium text-muted-foreground">Latest change</p>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <Badge variant="outline" className="text-xs uppercase tracking-wide">
          {update.object_type}
        </Badge>
        <span className="font-medium">{label}</span>
        <span className="text-muted-foreground">{update.action}</span>
        {update.source_tool ? (
          <span className="text-xs text-muted-foreground">via {update.source_tool}</span>
        ) : null}
      </div>
      {update.text ? (
        <p className="text-sm text-muted-foreground">{update.text}</p>
      ) : null}
    </div>
  )
}

function KV({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-foreground">{value}</span>
    </div>
  )
}

function DetailSkeleton() {
  return (
    <PageContainer>
      <div className="space-y-3">
        <Skeleton className="h-8 w-24" />
        <Skeleton className="h-7 w-2/3" />
        <Skeleton className="h-4 w-1/3" />
      </div>
      <div className="mt-6 grid grid-cols-1 gap-6 @3col:grid-cols-3">
        <div className="space-y-6 @3col:col-span-2">
          <Skeleton className="h-32 w-full rounded-xl" />
          <Skeleton className="h-24 w-full rounded-xl" />
        </div>
        <aside className="space-y-6">
          <Skeleton className="h-48 w-full rounded-xl" />
          <Skeleton className="h-32 w-full rounded-xl" />
        </aside>
      </div>
    </PageContainer>
  )
}
